/**
 * コマ単位の状態ラベル (AC-BIN-054)。
 *
 * `docs/specs/bidding/intraday-bidding.md` AC-BIN-054: 受付中 / GC 済 / 約定済 /
 * 受渡完了 / 未来コマ の 5 種。 同一コマに複数の状態が成立する場合の優先順位は
 * 受渡完了 > 約定済 > GC 済 > 受付中 > 未来コマ。
 *
 * 表示面は 2 つ (AC-BIN-054 本文):
 * - 札作成モーダル・入札編集モーダルのコマセレクト (AC-BIN-004 / AC-BIN-014)。
 *   GC 済は選択肢から除外し (`IntradayBidCreateModal.tsx` の `komaOptions` が
 *   `gcStatusOfKoma` で既に filter 済み)、 締切間近には既存の「⚠締切間近」 併記を使う。
 *   design (`design/bidding/intraday.html` `_buildSlotOptions`) も GC 済 / 締切間近の
 *   2 サフィックスのみで、 本 module の 5 状態ブラケット表記は使わない。
 * - 入札一覧のコマフィルタ (AC-BIN-002)。 選択肢ラベルの末尾に本 module が解決した
 *   状態を半角スペース + 半角カッコで後置する (#7603 Des 判断案 B)。 受付中 / 未来コマ
 *   は既定状態のため付けない ([[isKomaStatusLabelBracketed]])。
 */
import type { TFunction } from 'i18next'

import { jstWallClockToInstant } from '~/models/dateTime'
import { komaEndTime, komaNumberToTime } from '~/models/komaToTime'
import { isBeforeAcceptance } from '~/pages/IntradayBidding/models/acceptanceStart'
import { gcStatusOfKoma } from '~/pages/IntradayBidding/models/deliveryDate'

/** AC-BIN-054 の 5 状態。 */
export type KomaStatusLabel =
  | 'open' // 受付中
  | 'gc-closed' // GC 済
  | 'matched' // 約定済
  | 'completed' // 受渡完了
  | 'pre' // 未来コマ

/**
 * 受渡日 + コマ番号から、 そのコマの終了時刻 (= 受渡完了の境界) を絶対時刻で返す。
 * `~/models/komaToTime` の canonical な開始/終了時刻文字列を JST 壁時計として解釈する
 * (コマ 48 の終了は "24:00" = 翌日 00:00 へ桁上がりする。 `jstWallClockToInstant` の
 * `Date.UTC` 桁上がりに委ねる、 `gcCountdown.ts` と同じ方針)。
 */
const komaEndAt = (deliveryDate: Date, koma: number): Date => {
  const [hour, minute] = komaEndTime(komaNumberToTime(koma))
    .split(':')
    .map(Number)
  return jstWallClockToInstant(deliveryDate, hour, minute)
}

/**
 * コマ単位の状態ラベルを解決する (AC-BIN-054)。
 *
 * @param now - 現在時刻 (絶対時刻。 `~/models/clock` の `now()` 由来)
 * @param deliveryDate - 受渡日 (カレンダー日 `Date`)
 * @param koma - コマ番号 (1〜48)
 * @param hasMatchedBid - 当該コマを対象とする自社の札のうち、
 *   約定量が 0 より大きいものが 1 枚以上あるか (AC-BIN-054 の「約定済」定義そのもの、
 *   2026-09-14 issue #7280 PdM 判断: 1 枚でも約定していれば約定済)
 */
export const resolveKomaStatusLabel = (
  now: Date,
  deliveryDate: Date,
  koma: number,
  hasMatchedBid: boolean,
): KomaStatusLabel => {
  // 優先順位: 受渡完了 > 約定済 > GC 済 > 受付中 > 未来コマ。
  if (komaEndAt(deliveryDate, koma).getTime() <= now.getTime())
    return 'completed'
  if (hasMatchedBid) return 'matched'
  if (gcStatusOfKoma(now, deliveryDate, koma) === 'closed') return 'gc-closed'
  // GC 前 (open/imminent)。 受渡日そのものがまだ受付前 (AC-BIN-089c) なら
  // このコマも「未来コマ」 (= まだ入札の対象になっていない)。
  if (isBeforeAcceptance(now, deliveryDate)) return 'pre'
  return 'open'
}

/**
 * 入札一覧のコマフィルタ (AC-BIN-002) で括弧書き表示する対象か。
 *
 * #7603 Des 判断 (案 B)「状態ラベルが無いコマには何も付けない」: 受付中 / 未来コマ は
 * 大半のコマが該当する既定状態のため、 例外的な GC 済 / 約定済 / 受渡完了 のみ
 * ブラケットで可視化する。
 */
export const isKomaStatusLabelBracketed = (state: KomaStatusLabel): boolean =>
  state === 'gc-closed' || state === 'matched' || state === 'completed'

/** ブラケット表示するコマ状態ラベルの文言 (i18n)。 */
export const komaStatusLabelText = (
  state: KomaStatusLabel,
  t: TFunction,
): string => {
  switch (state) {
    case 'gc-closed':
      return t('page:biddingIntraday.komaFilter.stateGcClosed')
    case 'matched':
      return t('page:biddingIntraday.komaFilter.stateMatched')
    case 'completed':
      return t('page:biddingIntraday.komaFilter.stateCompleted')
    default:
      // 受付中 / 未来コマ は呼び出し側が [[isKomaStatusLabelBracketed]] で
      // 弾く前提 (= ここに来ないが、 型を network 的に総覧するため網羅する)。
      return ''
  }
}
